var _excluded = ["autoSelect", "closeOnSelect", "closeOnBlur", "defaultIsOpen", "isDisabled", "defaultValue", "id", "isLazy", "ref", "isOpen", "name", "onChange", "onClose", "onOpen", "value", "placeholder", "placement", "unavailable"],
    _excluded2 = ["onMouseEnter", "onMouseMove", "onMouseLeave", "onClick", "isDisabled", "isFocusable"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

/* eslint-disable max-len */
import { useClickable } from '@chakra-ui/clickable';
import { createDescendantContext } from '@chakra-ui/descendant';
import { useFormControl } from '@chakra-ui/form-control';
import { useControllableState, useDisclosure, useId, useIds, useOutsideClick, useShortcut, useUpdateEffect } from '@chakra-ui/hooks';
import { usePopper } from '@chakra-ui/popper';
import { createContext, mergeRefs } from '@chakra-ui/react-utils';
import { ariaAttr, callAllHandlers, dataAttr, focus, getNextItemFromSearch, getOwnerDocument, isActiveElement, normalizeEventKey } from '@chakra-ui/utils';
import React from 'react';
import { get12Hour, getHourObj } from './utils';
/* -------------------------------------------------------------------------------------------------
 * Create context to track descendants and their indices
 * -----------------------------------------------------------------------------------------------*/

export var [TimePickerDescendantsProvider, useTimePickerDescendantsContext, useTimePickerDescendants, useTimePickerDescendant] = createDescendantContext();
/* -------------------------------------------------------------------------------------------------
 * Create context to track time-picker state and logic
 * -----------------------------------------------------------------------------------------------*/

export var [TimePickerProvider, useTimePickerContext] = createContext({
  strict: false,
  name: 'TimePickerContext'
});
/* -------------------------------------------------------------------------------------------------
 * useTimePicker hook
 * -----------------------------------------------------------------------------------------------*/

/**
 * React Hook to manage a TimePicker
 *
 * It provides the logic and will be used with react context
 * to propagate its return value to all children
 */
export function useTimePicker(props) {
  var {
    autoSelect = true,
    closeOnSelect = true,
    closeOnBlur = true,
    defaultIsOpen,
    isDisabled = false,
    defaultValue,
    id = 'default',
    isLazy,
    ref: inputRefProp,
    isOpen: isOpenProp,
    name = 'time-picker',
    onChange,
    onClose: onCloseProp,
    onOpen: onOpenProp,
    value: valueProp,
    placeholder: placeholderProps,
    placement = 'bottom-start',
    unavailable = []
  } = props,
      popperProps = _objectWithoutPropertiesLoose(props, _excluded);

  var {
    isOpen,
    onOpen,
    onClose,
    onToggle
  } = useDisclosure({
    isOpen: isOpenProp,
    defaultIsOpen,
    onClose: onCloseProp,
    onOpen: onOpenProp
  });
  /**
   * Prepare the reference to the TimePicker and disclosure
   */

  var timePickerRef = React.useRef(null);
  var internalRef = React.useRef(null);
  var inputRef = mergeRefs(inputRefProp, internalRef);
  /**
   * Context to update the selected time value
   */

  var [selectedHour, setSelectedHour] = useControllableState({
    value: valueProp,
    defaultValue: defaultValue,
    onChange
  });
  useOutsideClick({
    ref: timePickerRef,
    handler: event => {
      var _timePickerRef$curren;

      if (isOpen && closeOnBlur && !((_timePickerRef$curren = timePickerRef.current) != null && _timePickerRef$curren.contains(event.target))) {
        onClose();
      }
    }
  });
  /**
   * Add some popper.js for dynamic positioning
   */

  var popper = usePopper(_extends({}, popperProps, {
    enabled: isOpen,
    placement
  }));
  var [focusedIndex, setFocusedIndex] = React.useState(0);
  /**
   * Context to register all TimePicker item nodes
   */

  var descendants = useTimePickerDescendants();
  /**
   * Generate unique ids for timePicker's list and input in input-group
   */

  var [InputGroupId, ListId] = useIds(id, 'time-picker-group', 'time-picker-list');
  /**
  * Focus the button when we close the menu
  */

  var {
    'aria-invalid': isInvalid
  } = useFormControl(props);
  var openAndFocusTimePicker = React.useCallback(() => {
    onOpen();
    focus(timePickerRef.current, {
      nextTick: true,
      selectTextIfInput: false
    });
  }, [onOpen, timePickerRef]);
  var openAndFocusFirstItem = React.useCallback(() => {
    onOpen();
    var first = descendants.firstEnabled();
    if (first) setFocusedIndex(first.index);
  }, [descendants, onOpen]);
  var openAndFocusLastItem = React.useCallback(() => {
    onOpen();
    var last = descendants.lastEnabled();
    if (last) setFocusedIndex(last.index);
  }, [onOpen, setFocusedIndex, descendants]);
  var motionCallback = React.useCallback(() => {
    var _timePickerRef$curren2, _descendants$item;

    var doc = getOwnerDocument(timePickerRef.current);
    var hasFocusWithin = (_timePickerRef$curren2 = timePickerRef.current) == null ? void 0 : _timePickerRef$curren2.contains(doc.activeElement);
    var shouldRefocus = isOpen && !hasFocusWithin;
    if (!shouldRefocus) return;
    var node = (_descendants$item = descendants.item(focusedIndex)) == null ? void 0 : _descendants$item.node;

    if (node) {
      focus(node, {
        selectTextIfInput: false,
        preventScroll: false
      });
    }
  }, [descendants, focusedIndex, isOpen]);
  return {
    autoSelect,
    closeOnBlur,
    closeOnSelect,
    descendants,
    focusedIndex,
    isDisabled,
    forceUpdate: popper.forceUpdate,
    hoursList: getHourObj(),
    InputGroupId,
    inputRef,
    isInvalid,
    isLazy,
    isOpen,
    name,
    onClose,
    onOpen,
    onToggle,
    onTransitionEnd: motionCallback,
    openAndFocusFirstItem,
    openAndFocusLastItem,
    openAndFocusTimePicker,
    orientation: 'vertical',
    placeholderProps,
    popper,
    selectedHour,
    setFocusedIndex,
    setSelectedHour,
    ListId,
    timePickerRef,
    unavailable
  };
}
export function useTimePickerInputGroup(props, externalRef) {
  if (externalRef === void 0) {
    externalRef = null;
  }

  var timePicker = useTimePickerContext();
  var {
    autoSelect,
    InputGroupId,
    isOpen,
    isDisabled,
    isInvalid,
    onClose,
    popper,
    openAndFocusFirstItem,
    openAndFocusLastItem,
    openAndFocusTimePicker
  } = timePicker;
  var onClick = React.useCallback(() => {
    if (isDisabled) return;

    if (isOpen) {
      onClose();
    } else {
      var action = autoSelect ? openAndFocusFirstItem : openAndFocusTimePicker;
      action();
    }
  }, [autoSelect, isDisabled, isOpen, onClose, openAndFocusFirstItem, openAndFocusTimePicker]);
  var onKeyDown = React.useCallback(event => {
    var eventKey = normalizeEventKey(event);
    var keyMap = {
      Enter: openAndFocusFirstItem,
      ArrowDown: openAndFocusFirstItem,
      ArrowUp: openAndFocusLastItem
    };
    var action = keyMap[eventKey];

    if (action) {
      event.preventDefault();
      event.stopPropagation();
      action(event);
    }
  }, [openAndFocusFirstItem, openAndFocusLastItem]);

  var groupProps = _extends({}, props, {
    id: InputGroupId,
    'aria-controls': InputGroupId,
    'aria-disabled': ariaAttr(isDisabled),
    'aria-expanded': isOpen,
    'aria-haspopup': 'menu',
    'aria-invalid': ariaAttr(isInvalid),
    'data-active': dataAttr(isOpen),
    'data-invalid': dataAttr(isInvalid),
    'data-test-id': InputGroupId,
    onClick: callAllHandlers(props.onClick, onClick),
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
    ref: mergeRefs(timePicker.inputRef, externalRef, popper.referenceRef)
  });

  return groupProps;
}
export function useTimePickerInput(props, externalRef) {
  if (externalRef === void 0) {
    externalRef = null;
  }

  var timePicker = useTimePickerContext();
  var {
    inputRef,
    InputGroupId,
    placeholderProps,
    selectedHour
  } = timePicker;
  var {
    register
  } = useTimePickerDescendant();
  var formattedValue = get12Hour(selectedHour);

  var inputProps = _extends({}, props, {
    id: InputGroupId + "-input",
    'data-test-id': InputGroupId + "-input",
    placeholder: placeholderProps || 'Select time',
    value: formattedValue,
    ref: mergeRefs(register, inputRef, externalRef)
  });

  return inputProps;
}

function isTargetMenuItem(target) {
  var _target$getAttribute;

  // this will catch `menuItem`
  return target instanceof HTMLElement && !!((_target$getAttribute = target.getAttribute('role')) != null && _target$getAttribute.startsWith('menuItem'));
}
/* -------------------------------------------------------------------------------------------------
 * useTimePickerList
 * -----------------------------------------------------------------------------------------------*/


/**
 * React Hook to manage a TimePicker list.
 *
 * The assumption here is that the `useTimePicker` hook is used
 * in a component higher up the tree, and its return value
 * is passed as `context` to this hook.
 */
export function useTimePickerList(props, ref) {
  if (ref === void 0) {
    ref = null;
  }

  var timePicker = useTimePickerContext();

  if (!timePicker) {
    throw new Error('useTimePickerContext: context is undefined. Seems you forgot to wrap component within <TimePicker>');
  }

  var {
    focusedIndex,
    hoursList,
    onClose,
    setFocusedIndex,
    setSelectedHour,
    ListId,
    timePickerRef,
    unavailable
  } = timePicker;
  var descendants = useTimePickerDescendantsContext();
  /**
   * Hook that creates a keydown event handler that listens
   * to printable keyboard character press
   */

  var createTypeaheadHandler = useShortcut({
    preventDefault: event => {
      var isSelectItem = isTargetMenuItem(event.target);
      return event.key !== ' ' && isSelectItem;
    }
  });
  var onKeyDown = React.useCallback(event => {
    var eventKey = normalizeEventKey(event);
    var keyMap = {
      Tab: event => event.preventDefault(),
      Escape: onClose,
      ArrowDown: () => {
        var next = descendants.nextEnabled(focusedIndex);
        if (next) setFocusedIndex(next.index);
      },
      ArrowUp: () => {
        var prev = descendants.prevEnabled(focusedIndex);
        if (prev) setFocusedIndex(prev.index);
      }
    };
    var fn = keyMap[eventKey];

    if (fn) {
      event.preventDefault();
      fn(event);
      return;
    }

    var onTypeahead = createTypeaheadHandler(character => {
      /**
       * Typeahead: Based on current character pressed,
       * find the next item to be selected
       */
      var nextItem = getNextItemFromSearch(descendants.values(), character, item => {
        var _item$node$textConten, _item$node;

        return (_item$node$textConten = item == null ? void 0 : (_item$node = item.node) == null ? void 0 : _item$node.textContent) != null ? _item$node$textConten : '';
      }, descendants.item(focusedIndex));

      if (nextItem) {
        var index = descendants.indexOf(nextItem.node);
        setFocusedIndex(index);
      }
    });

    if (isTargetMenuItem(event.target)) {
      onTypeahead(event);
    }
  }, [createTypeaheadHandler, descendants, focusedIndex, onClose, setFocusedIndex]);

  var timePickerlistProps = _extends({}, props, {
    ref: mergeRefs(timePickerRef, ref),
    children: props.children,
    tabIndex: -1,
    role: 'menu',
    id: ListId,
    hoursList: hoursList,
    unavailable: unavailable,
    setSelectedHour: setSelectedHour,
    'data-test-id': ListId,
    style: _extends({}, props.style, {
      transformOrigin: 'var(--popper-transform-origin)'
    }),
    'aria-orientation': 'vertical',
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown)
  });

  return timePickerlistProps;
}
/* -------------------------------------------------------------------------------------------------
 * useTimePickerPositioner: Composes usePopper to position the menu
 * -----------------------------------------------------------------------------------------------*/

export function useTimePickerPositioner(props) {
  if (props === void 0) {
    props = {};
  }

  var {
    popper,
    isOpen
  } = useTimePickerContext();
  return popper.getPopperProps(_extends({}, props, {
    style: _extends({
      visibility: isOpen ? 'visible' : 'hidden'
    }, props.style)
  }));
}
/* -------------------------------------------------------------------------------------------------
 * useTimePickerItem: Hook for each menu/hour item within the time picker list.
 * -----------------------------------------------------------------------------------------------*/

export function useTimePickerItem(props, externalRef) {
  if (externalRef === void 0) {
    externalRef = null;
  }

  var {
    onMouseEnter: onMouseEnterProp,
    onMouseMove: onMouseMoveProp,
    onMouseLeave: onMouseLeaveProp,
    onClick: onClickProp,
    isDisabled,
    isFocusable
  } = props,
      htmlProps = _objectWithoutPropertiesLoose(props, _excluded2);

  var timePicker = useTimePickerContext();
  var {
    closeOnSelect,
    focusedIndex,
    isOpen,
    onClose,
    setFocusedIndex,
    timePickerRef
  } = timePicker;
  var ref = React.useRef(null); // NOTE: causing mismatch of ids: https://github.com/chakra-ui/chakra-ui/issues/3020

  var id = "time-picker-item-" + useId();
  /**
   * Register the TimePickerItem's node into the domContext
   */

  var {
    index,
    register
  } = useTimePickerDescendant();
  var onMouseEnter = React.useCallback(event => {
    onMouseEnterProp == null ? void 0 : onMouseEnterProp(event);
    if (isDisabled) return;
    setFocusedIndex(index);
  }, [setFocusedIndex, index, isDisabled, onMouseEnterProp]);
  var onMouseMove = React.useCallback(event => {
    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);

    if (document.activeElement !== ref.current) {
      onMouseEnter(event);
    }
  }, [onMouseEnter, onMouseMoveProp]);
  var onMouseLeave = React.useCallback(event => {
    onMouseLeaveProp == null ? void 0 : onMouseLeaveProp(event);
    if (isDisabled) return;
    setFocusedIndex(-1);
  }, [setFocusedIndex, isDisabled, onMouseLeaveProp]);
  var onClick = React.useCallback(event => {
    if (isDisabled) return;
    onClickProp == null ? void 0 : onClickProp(event);
    if (!isTargetMenuItem(event.target)) return;
    /**
     * Close menu and parent menus, allowing the MenuItem
     * to override its parent menu's `closeOnSelect` prop.
     */

    if (closeOnSelect) {
      onClose();
    }
  }, [isDisabled, onClickProp, closeOnSelect, onClose]);
  var isFocused = index === focusedIndex;
  useUpdateEffect(() => {
    if (!isOpen) return;

    if (isFocused && ref.current) {
      focus(ref.current, {
        nextTick: true,
        selectTextIfInput: false
      });
    } else if (timePickerRef.current && !isActiveElement(timePickerRef.current)) {
      focus(timePickerRef.current);
    }
  }, [isFocused, timePickerRef, isOpen]);
  var clickableProps = useClickable({
    onClick,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
    isFocusable,
    isDisabled
  });
  return _extends({
    id
  }, htmlProps, clickableProps, {
    'data-test-id': id,
    ref: mergeRefs(register, ref, externalRef),
    role: 'menuItem',
    tabIndex: isFocused ? 0 : -1
  });
}
//# sourceMappingURL=use-time-picker.js.map